"use strict";
/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StorageEventEmitter = void 0;
const lz_string_1 = __importDefault(require("lz-string"));
const TabLogging_1 = __importStar(require("./TabLogging"));
class StorageEventEmitter {
    /**
     * Create a new EventEmitter
     */
    constructor(key) {
        this.listeners = [];
        this.listenersOnce = [];
        this.key = key;
        this.emit = this.emit.bind(this);
        this.on = this.on.bind(this);
        this.once = this.once.bind(this);
        this.sendProxy = this.sendProxy.bind(this);
        this.triggerStorageEvent = this.triggerStorageEvent.bind(this);
        this.tabLog = new TabLogging_1.default("StorageEvent", this.sendProxy);
        window.addEventListener("storage", (stev) => {
            // only listen to events related to own key,
            // and don't listen to cleanup calls
            if (stev.key !== this.key || stev.newValue === null) {
                return;
            }
            const decompressed = lz_string_1.default.decompressFromUTF16(stev.newValue) || "{}";
            const parsed = JSON.parse(decompressed);
            // Against convention Safari does recognize storage events from own
            // context/window. At least I cannot see any 'other' contexts here
            if (parsed.tabId === this.tabInfo.ownId) {
                return;
            }
            if (TabLogging_1.isBusControlMessage(parsed.data)) {
                this.tabLog.handleBusControlResponse(parsed.data);
            }
            else {
                this.invokeListeners(parsed.data);
            }
        });
        this.tabLog.sayHello();
    }
    /**
     * test wether localStorage is present, and works, and is not set off by the user (browser setting) or incognito mode.
     * https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#testing_for_availability
     */
    static isSupported() {
        try {
            const storage = window.localStorage;
            const x = "__local_storage_test__";
            storage.setItem(x, x);
            storage.removeItem(x);
            return true;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Emits the event to its subscribers
     * @param {Payload} payload
     */
    emit(payload) {
        this.triggerStorageEvent(payload);
        this.invokeListeners(payload);
    }
    /**
     * Unsubscribes the listener from the event
     * @param {Listener<Payload>} listener
     */
    off(listener) {
        const listenerIndex = this.listeners.indexOf(listener);
        if (listenerIndex > -1) {
            this.listeners.splice(listenerIndex, 1);
        }
    }
    /**
     * Subscribes a listener to the event
     * @param {Listener<Payload>} listener
     */
    on(listener) {
        this.listeners.push(listener);
    }
    /**
     * Subscribes the listener to receive the event exactly once
     * @param {Listener<Payload>} listener
     */
    once(listener) {
        this.listenersOnce.push(listener);
    }
    /**
     * Information about the tabs involved in storage event emitting
     * @returns {IBusLogging}
     */
    get tabInfo() {
        return this.tabLog.tabLog;
    }
    /**
     * Broadcasts without invoking own listeners
     * Only needed for bus control purposes
     * @param busControl
     * @private
     */
    sendProxy(busControl) {
        this.triggerStorageEvent(busControl);
    }
    /**
     * Sends `data` across tabs by firing StorageEvent using `window.localStorage`.
     * `data` is stringified and then lzipped
     * @param data
     * @private
     */
    triggerStorageEvent(data) {
        // data is sent with tab id, so certain browsers
        // don't listen to own message (see listener in ctor)
        const pack = {
            data,
            tabId: this.tabInfo.ownId,
        };
        const compressed = lz_string_1.default.compressToUTF16(JSON.stringify(pack));
        window.localStorage.setItem(this.key, compressed);
        // cleanup
        setTimeout(() => window.localStorage.removeItem(this.key), 500);
    }
    invokeListeners(payload) {
        for (const listener of [...this.listeners, ...this.listenersOnce]) {
            listener(payload);
        }
        this.listenersOnce = [];
    }
}
exports.StorageEventEmitter = StorageEventEmitter;
exports.default = StorageEventEmitter;
